import { getJson } from '../util/fetch-json.js';
import { init, prospect } from '../state/app-state.js';

const KEY_RE = /^[a-z0-9-]{1,32}$/;

export interface InitPanelHandlers {
  onLoad: (key: string) => Promise<void>;
  onInitialize: (key: string, prompt: string) => Promise<void>;
}

export function renderInitPanel(host: HTMLElement, handlers: InitPanelHandlers): void {
  host.innerHTML = `
    <div class="panel">
      <h2>Initialization</h2>
      <div class="row">
        <label>
          <span>Prospect Key</span>
          <input id="ip-key" type="text" placeholder="e.g. citizens" maxlength="32" />
        </label>
        <label>
          <span>Company Prompt</span>
          <input id="ip-prompt" type="text" value="A bank called 'ABC Bank'" />
        </label>
        <button id="ip-go" class="button is-primary" disabled>Initialize new demo</button>
      </div>
      <ul id="ip-progress" class="progress" hidden></ul>
      <div id="ip-error" class="login-error"></div>
    </div>`;

  const keyInput = host.querySelector<HTMLInputElement>('#ip-key')!;
  const promptInput = host.querySelector<HTMLInputElement>('#ip-prompt')!;
  const button = host.querySelector<HTMLButtonElement>('#ip-go')!;
  const progress = host.querySelector<HTMLUListElement>('#ip-progress')!;
  const errorEl = host.querySelector<HTMLDivElement>('#ip-error')!;

  let mode: 'new' | 'existing' = 'new';
  let knownKeys = new Set<string>();

  async function refreshKnown() {
    try {
      const r = await getJson<{ keys: string[] }>('/api/prospects');
      knownKeys = new Set(r.keys);
      updateMode();
    } catch { /* ignore */ }
  }

  function updateMode() {
    const k = keyInput.value.trim();
    const valid = KEY_RE.test(k);
    button.disabled = !valid;
    if (!valid) { button.textContent = 'Enter a valid key'; return; }
    mode = knownKeys.has(k) ? 'existing' : 'new';
    button.textContent = mode === 'existing' ? 'Load existing demo' : 'Initialize new demo';
    promptInput.disabled = mode === 'existing';
  }

  let debounce: number | undefined;
  keyInput.addEventListener('input', () => {
    if (debounce) window.clearTimeout(debounce);
    debounce = window.setTimeout(refreshKnown, 250);
    updateMode();
  });

  button.addEventListener('click', async () => {
    errorEl.textContent = '';
    const k = keyInput.value.trim();
    if (!KEY_RE.test(k)) return;
    button.disabled = true;
    try {
      if (mode === 'existing') {
        init.set({ phase: 'loading', key: k });
        await handlers.onLoad(k);
      } else {
        progress.hidden = false;
        init.set({ phase: 'generating', key: k, completed: 0, total: 8 });
        await handlers.onInitialize(k, promptInput.value);
      }
      prospect.set(k);
      document.cookie = `sgt_prospect=${encodeURIComponent(k)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
    } catch (e) {
      errorEl.textContent = e instanceof Error ? e.message : 'Init failed';
      init.set({ phase: 'awaiting' });
    } finally {
      button.disabled = false;
    }
  });

  const panelHost = host.querySelector<HTMLElement>('.panel')!;
  init.subscribe((s) => {
    if (s.phase === 'generating') {
      progress.hidden = false;
      progress.innerHTML = `<li>${s.completed}/${s.total} assets generated</li>`;
      panelHost.classList.remove('is-collapsed');
    } else if (s.phase === 'ready') {
      progress.hidden = true;
      panelHost.classList.add('is-collapsed');
    } else {
      progress.hidden = true;
      panelHost.classList.remove('is-collapsed');
    }
  });

  void refreshKnown();
}
